import { useState } from "react";
import { Alert, CircularProgress } from "@mui/material";
import MDBox from "../../controls/MDBox";
import MDTypography from "../../controls/MDTypography";
import MDButton from "../../controls/MDButton";
import { nombreDia } from "../CalendarioGestion/calendarioComun";

// Por qué le tocó al gestor un caso de ese organismo, según lo que decía el Calendario de Gestores el día que se le asignó (no hoy:
// la propuesta sigue abierta hasta que cargue un evento). Devuelve "" si ese día no tenía organismo cargado.
const explicacionCalendario = (datos) => {
  const calendario = datos?.calendario;
  if (!calendario?.organismos?.length) return "";

  const organismos = calendario.organismos.join(" / ");
  const cuando = calendario.esHoy ? "Hoy te toca" : `El ${nombreDia(calendario.diaSemana).toLowerCase()} te tocaba`;
  const inicio = `${cuando} ${organismos} según el Calendario de Gestores`;
  if (calendario.delOrganismoDelDia) return `${inicio}, y este caso es de ese organismo.`;

  const quedan = calendario.esHoy ? "quedan" : "quedaban";
  const deEsos = calendario.organismos.length > 1 ? "esos organismos" : "ese organismo";
  return `${inicio}, pero ya no ${quedan} casos de ${deEsos} para proponerte: este caso es de ${datos.organismo || "otro organismo"} y se asignó por puntaje.`;
};

// Fecha y hora del servidor tal como vienen (sin convertir de zona), igual que las fechas de los eventos en la grilla.
// Devuelve "el 20/10/2026 a las 14:35", o "" si el valor falta o no es una fecha.
const fechaHoraLegible = (valor) => {
  if (!valor) return "";
  const fecha = new Date(valor);
  if (Number.isNaN(fecha.getTime())) return "";
  const dia = fecha.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
  const hora = fecha.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  return `el ${dia} a las ${hora}`;
};

// Panel del Disparador Automático de Gestiones en Gestión Pre-Judicial (plan 3.2d, paso 3): muestra el caso que el sistema le
// asignó al gestor y deja pausarlo, retomarlo o pedir uno nuevo. La propuesta vive en el backend; este panel solo la refleja.
//
//  - ACTIVA: el caso asignado es el único que se ve y los filtros quedan bloqueados hasta cargar un evento o pausar.
//  - PAUSADA: el gestor pausó para atender otros casos; un aviso fijo le recuerda el pendiente y lo retoma desde acá.
//  - SIN_PROPUESTA: si hoy es obligatorio y no hay casos, se explica; si puede pedir un caso, se ofrece el botón.
function PanelPropuesta({ propuesta, error, procesando, onPausar, onRetomar, onPedir, onReintentar }) {
  const [errorOculto, setErrorOculto] = useState(false);

  const aviso = error && !errorOculto ? (
    <Alert severity="warning" onClose={() => setErrorOculto(true)} sx={{ mb: 2 }}>
      No se pudo consultar el Disparador de gestiones: {error}
    </Alert>
  ) : null;

  // Disparador apagado por un administrador: la pantalla funciona como antes, sin panel ni botón «Pedir un caso».
  if (!propuesta || propuesta.disparadorActivo === false) return aviso;

  const datos = propuesta.propuesta;
  const botonEstilo = { whiteSpace: "nowrap" };

  if (propuesta.estado === "ACTIVA" && datos) {
    return (
      <>
        {aviso}
        <Alert
          severity="info"
          sx={{ mb: 2, alignItems: "center" }}
          action={
            <MDButton variant="contained" color="info" size="small" onClick={onPausar} disabled={procesando} sx={botonEstilo}>
              {procesando ? <CircularProgress size={16} color="inherit" /> : "Pausar"}
            </MDButton>
          }
        >
          <MDTypography variant="button" fontWeight="bold" display="block">
            Caso asignado: #{datos.idCaso}
            {fechaHoraLegible(datos.asignadaEl) && <span style={{ fontWeight: "normal" }}> · asignado {fechaHoraLegible(datos.asignadaEl)}</span>}
          </MDTypography>
          {datos.esPrioritario && (
            <MDTypography variant="caption" display="block" fontWeight="bold">
              Caso prioritario: lo marcó un líder con la estrella y el sistema lo propone primero.
            </MDTypography>
          )}
          {explicacionCalendario(datos) && (
            <MDTypography variant="caption" display="block">
              {explicacionCalendario(datos)}
            </MDTypography>
          )}
          <MDTypography variant="caption" display="block">
            Gestioná este caso y cargá un evento para recibir el siguiente. Los filtros están bloqueados; si necesitás atender otros
            casos, pausá y retomalo después.
          </MDTypography>
        </Alert>
      </>
    );
  }

  if (propuesta.estado === "PAUSADA" && datos) {
    return (
      <>
        {aviso}
        <Alert
          severity="warning"
          sx={{ mb: 2, alignItems: "center" }}
          action={
            <MDButton variant="contained" color="warning" size="small" onClick={onRetomar} disabled={procesando} sx={botonEstilo}>
              {procesando ? <CircularProgress size={16} color="inherit" /> : "Retomar"}
            </MDButton>
          }
        >
          <MDTypography variant="button" fontWeight="bold" display="block">
            Tenés el caso #{datos.idCaso} pendiente
            {fechaHoraLegible(datos.pausadaDesde) && <span style={{ fontWeight: "normal" }}> · pausado {fechaHoraLegible(datos.pausadaDesde)}</span>}
          </MDTypography>
          {explicacionCalendario(datos) && (
            <MDTypography variant="caption" display="block">
              {explicacionCalendario(datos)}
            </MDTypography>
          )}
          <MDTypography variant="caption" display="block">
            El sistema no te asigna otro caso hasta que cargues un evento sobre este.
          </MDTypography>
        </Alert>
      </>
    );
  }

  // Sin propuesta.
  if (propuesta.obligatorioHoy) {
    return (
      <>
        {aviso}
        <Alert
          severity="info"
          sx={{ mb: 2, alignItems: "center" }}
          action={
            <MDButton variant="contained" color="info" size="small" onClick={onReintentar} disabled={procesando} sx={botonEstilo}>
              {procesando ? <CircularProgress size={16} color="inherit" /> : "Reintentar"}
            </MDButton>
          }
        >
          {propuesta.mensaje || "Hoy el sistema te asigna los casos, pero por ahora no hay ninguno para proponerte."}
        </Alert>
      </>
    );
  }

  if (propuesta.puedePedir) {
    // Si se pidió un caso y no había ninguno para proponer, el motivo se ve bien (no como una nota al lado del botón).
    if (propuesta.mensaje) {
      return (
        <>
          {aviso}
          <Alert
            severity="info"
            sx={{ mb: 2, alignItems: "center" }}
            action={
              <MDButton variant="contained" color="info" size="small" onClick={onPedir} disabled={procesando} sx={botonEstilo}>
                {procesando ? <CircularProgress size={16} color="inherit" /> : "Pedir un caso"}
              </MDButton>
            }
          >
            {propuesta.mensaje}
          </Alert>
        </>
      );
    }
    return (
      <>
        {aviso}
        <MDBox mb={2} display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <MDButton variant="contained" color="info" size="small" onClick={onPedir} disabled={procesando} sx={botonEstilo}>
            {procesando ? <CircularProgress size={16} color="inherit" /> : "Pedir un caso"}
          </MDButton>
          <MDTypography variant="caption" color="text">
            El sistema te propone el caso con más prioridad entre los tuyos.
          </MDTypography>
        </MDBox>
      </>
    );
  }

  return aviso;
}

export default PanelPropuesta;
